"use client";

import React, { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useRouter as useNextRouter } from "next/navigation";
import {
  Eye,
  EyeOff,
  ShieldCheck,
  Lock,
  CheckCircle2,
  AlertCircle,
  Building2,
  Mail,
  User,
} from "lucide-react";

const GoogleIcon = () => (
  <svg className="h-5 w-5 mr-2.5 shrink-0" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17Z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.31 0 6.09-1.1 8.12-2.98l-3.88-3.05c-1.1.74-2.51 1.18-4.24 1.18-3.27 0-6.04-2.21-7.03-5.18H.94v3.16C3 21.2 7.21 24 12 24Z"
    />
    <path
      fill="#FBBC05"
      d="M4.97 14.02c-.25-.74-.39-1.54-.39-2.35s.14-1.61.39-2.35V6.16H.94C.34 7.36 0 8.74 0 10.17s.34 2.81.94 4.01l4.03-3.16Z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.8 0 3.42.62 4.69 1.83l3.52-3.52C18.09 1.14 15.31 0 12 0 7.21 0 3 2.8 .94 6.84l4.03 3.16C5.96 7.03 8.73 4.75 12 4.75Z"
    />
  </svg>
);

export default function SignupPage() {
  const router = useNextRouter();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [firmName, setFirmName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (password !== confirmPassword) {
      setErrorMsg("Passwords do not match. Please re-enter.");
      return;
    }

    if (password.length < 8) {
      setErrorMsg("Password must be at least 8 characters long.");
      return;
    }

    setIsLoading(true);

    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: fullName,
          email,
          firm_name: firmName,
          password,
        }),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        setErrorMsg(data.error || "Failed to create account.");
      } else {
        setSuccessMsg("Account created! Logging you in & setting up your profile...");
        // Auto login
        const loginRes = await fetch("/api/auth/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });
        const loginData = await loginRes.json();
        if (loginData.token) {
          localStorage.setItem("canlexa_jwt", loginData.token);
        }
        setTimeout(() => {
          window.location.href = "/profile?onboarding=true";
        }, 1200);
      }
    } catch {
      setErrorMsg("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    window.location.href = "/api/auth/google/login";
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-700 font-sans">
      {/* Top Header */}
      <header className="w-full flex justify-between items-center pt-6 pb-4 px-6 sm:px-10">
        <Link href="/" className="flex items-center gap-2.5 font-bold text-lg text-slate-900 tracking-tight">
          <img
            src="/canlexa_maple_logo_v2.png"
            alt="CanLexa Logo"
            className="w-8 h-8 rounded-lg object-contain"
          />
          <span className="text-xl font-extrabold tracking-tight">CanLexa</span>
        </Link>
        <div className="text-xs text-slate-500 font-medium">
          Already have an account?{" "}
          <Link href="/login" className="text-blue-600 font-bold hover:underline">
            Sign in
          </Link>
        </div>
      </header>

      {/* Main Form Container */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 my-auto py-8">
        <div className="w-full max-w-[520px] bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-200/80 p-6 sm:p-10 transition-all">
          <h2 className="mb-2 text-2xl sm:text-3xl font-bold text-center text-slate-900 tracking-tight">
            Create your CanLexa account
          </h2>
          <p className="text-xs text-slate-500 text-center mb-6">
            Join Ontario litigation firms in automating legal research and drafting.
          </p>

          {/* Success / Error Messages */}
          {successMsg && (
            <div className="mb-5 p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-xl flex items-center gap-2 font-medium">
              <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}

          {errorMsg && (
            <div className="mb-5 p-3.5 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center gap-2 font-medium">
              <AlertCircle size={16} className="text-red-600 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Google SSO Button */}
          <div className="mb-6 space-y-3">
            <a
              href="/api/auth/google/login"
              className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded-lg text-sm font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors shadow-sm cursor-pointer no-underline block text-center"
            >
              <GoogleIcon />
              <span>Sign up with Google</span>
            </a>
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-slate-500 font-medium">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label htmlFor="full_name" className="block text-xs font-semibold text-slate-700 mb-1">
                Full Name
              </label>
              <div className="relative">
                <input
                  id="full_name"
                  type="text"
                  required
                  placeholder="e.g. Maria Delgado"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="h-11 block w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F172A] focus:border-transparent transition-all"
                />
                <User size={16} className="absolute left-3.5 top-3.5 text-slate-400" />
              </div>
            </div>

            <div>
              <label htmlFor="user_email" className="block text-xs font-semibold text-slate-700 mb-1">
                Work Email Address
              </label>
              <div className="relative">
                <input
                  id="user_email"
                  type="email"
                  required
                  placeholder="you@lawfirm.ca"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 block w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F172A] focus:border-transparent transition-all"
                />
                <Mail size={16} className="absolute left-3.5 top-3.5 text-slate-400" />
              </div>
            </div>

            <div>
              <label htmlFor="firm_name" className="block text-xs font-semibold text-slate-700 mb-1">
                Law Firm / Organization
              </label>
              <div className="relative">
                <input
                  id="firm_name"
                  type="text"
                  placeholder="e.g. Toronto Litigation LLP"
                  value={firmName}
                  onChange={(e) => setFirmName(e.target.value)}
                  className="h-11 block w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F172A] focus:border-transparent transition-all"
                />
                <Building2 size={16} className="absolute left-3.5 top-3.5 text-slate-400" />
              </div>
            </div>

            <div>
              <label htmlFor="user_password" className="block text-xs font-semibold text-slate-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="user_password"
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 block w-full pl-10 pr-11 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F172A] focus:border-transparent transition-all"
                />
                <Lock size={16} className="absolute left-3.5 top-3.5 text-slate-400 pointer-events-none" />
                <button
                  type="button"
                  onClick={() =>
                    setShowPassword((prev) => {
                      const next = !prev;
                      console.log("Eye icon clicked, showPassword:", next);
                      return next;
                    })
                  }
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors z-10 cursor-pointer"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirm_password" className="block text-xs font-semibold text-slate-700 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-11 block w-full pl-10 pr-11 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F172A] focus:border-transparent transition-all"
                />
                <Lock size={16} className="absolute left-3.5 top-3.5 text-slate-400 pointer-events-none" />
                <button
                  type="button"
                  onClick={() =>
                    setShowConfirmPassword((prev) => {
                      const next = !prev;
                      console.log("Confirm Eye icon clicked, showConfirmPassword:", next);
                      return next;
                    })
                  }
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors z-10 cursor-pointer"
                  aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="mt-6 h-11 w-full flex items-center justify-center py-3 px-4 rounded-lg text-sm font-semibold text-white bg-[#0F172A] hover:bg-[#1E293B] active:bg-[#090E17] focus:outline-none focus:ring-2 focus:ring-[#0F172A] focus:ring-offset-2 transition-all shadow-sm disabled:opacity-60 cursor-pointer"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Creating account...</span>
                </div>
              ) : (
                "Create Account"
              )}
            </button>
          </form>


        </div>
      </main>

      <footer className="w-full text-center text-xs py-4 text-slate-400 border-t border-slate-100">
        <p>© 2026 CanLexa Inc. All rights reserved. Ontario Law Society & PIPEDA Compliant.</p>
      </footer>
    </div>
  );
}
